import React, { useState, useMemo, useCallback, useEffect, useContext, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { secureDelete, secureGetPdf, secureGet, securePost } from "../../config/api";
import { confAxios } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ProductRow from "../../components/ProductRow";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";
import DetailsHeader from "../../components/DetailsHeader";
import ModuleWrapper from "../../components/ModuleWrapper";
import { DoubleButton } from "../../components/PrimaryButton";
import { Menu, MenuItem } from '@mui/material';
import checkIcon from "../../icons/check.svg";
import PrintIcon from '../../icons/printer.svg';
import closeIcon from '../../icons/xB.svg';
import ChevronDownIcon from '../../icons/chevron-down.svg';
import UserContext from "../../Shared/UserContext";

const InvoiceDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { state: { role } } = useContext(UserContext);
  const [showModule, setShowModule] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [markingAsPaid, setMarkingAsPaid] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [downloadingBonSortie, setDownloadingBonSortie] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [invoice, setInvoice] = useState({});
  const [vehicle, setVehicle] = useState(null);
  const [quote, setQuote] = useState(null);
  const [showPaymentImageModal, setShowPaymentImageModal] = useState(false);
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchVehicle = useCallback(async (vehicleId) => {
    if (!vehicleId) return;

    const abortController = new AbortController();
    try {
      const response = await secureGet(`/vehicles/${vehicleId}`, showSnackbar, {
        signal: abortController.signal
      });
      setVehicle(response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch vehicle:", error);
      }
    }
  }, []);

  const fetchQuote = useCallback(async (receptionDocumentId) => {
    if (!receptionDocumentId) return;

    const abortController = new AbortController();
    try {
      const response = await secureGet(`/quotes/reception-document/${receptionDocumentId}`, showSnackbar, {
        signal: abortController.signal
      });
      setQuote(response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch quote:", error);
        // Handle 404 silently (no quote found)
        if (error.response?.status === 404) {
          setQuote(null);
        }
      }
    }
  }, []);

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/bills/${id}`, showSnackbar, { signal });
      const fetchedInvoice = response.data;
      
      setInvoice(fetchedInvoice);
      console.log("fetchedInvoice", fetchedInvoice);

      // Fetch vehicle if vehicleId exists
      if (fetchedInvoice?.vehicleId) {
        await fetchVehicle(fetchedInvoice.vehicleId);
      }

      // Fetch quote if receptionDocumentId exists
      if (fetchedInvoice?.receptionDocumentId) {
        await fetchQuote(fetchedInvoice.receptionDocumentId);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch invoice:", error);
      }
    } finally {
      setLoading(false);
      setMarkingAsPaid(false);
    }
  }, [id, signal, fetchVehicle, fetchQuote]);

  useEffect(() => {
      fetchInvoice();

      return () => {
        abortController.abort();
      };
  }, [id, abortController, fetchInvoice]);


  const handleMarkPaidClick = () => {
    setShowModule(true);
  };

  const handleModuleClose = useCallback((action) => {
    if (action === "submit") {
      // Set loading state for marking as paid
      setMarkingAsPaid(true);
      // Refresh invoice data after payment is marked
      fetchInvoice();
    }
    setShowModule(false);
  }, [fetchInvoice]);

  const handlePaymentModeClick = useCallback(() => {
    console.log('Payment mode clicked, opening modal', { justificationFile: invoice?.justificationFile });
    setShowPaymentImageModal(true);
  }, [invoice?.justificationFile]);

  if (loading && !invoice) return <LoadingLogo />;

  // ---- Header Props ----
  const headerProps = {
    entityId: invoice?.billNumber || "—",
    status: t(`bill_status.${invoice?.paymentStatus}`),
    rawStatus: invoice?.paymentStatus,
    client: { code: invoice.clientCode, name: invoice.clientName, to: `/clients/details/${invoice.clientId}` },
    complementary: { id: invoice.projectNumber, to: `/projects/details/${invoice.projectId}` },
    paymentMode: invoice?.paymentMode ? t(`paymentModes.${invoice.paymentMode}`) : null,
    justificationFile: invoice?.justificationFile ?? null,
    onPaymentModeClick: invoice?.justificationFile ? handlePaymentModeClick : undefined, 
    createdAt: invoice?.createdAt ?? null,
    fuelPercent: null,
    mileage: null,
    vehicle: vehicle ? {
      brand: vehicle?.brand?.name || vehicle?.brand || '—',
      model: vehicle?.model?.name || '—',
      year: vehicle?.year || '—',
      type: vehicle?.type || '—',
      color: vehicle?.color || '—',
      plate: vehicle?.immatriculationNumber || '—',
      vin: vehicle?.vinNumber || '—',
    } : null,
  };

    const handleDownloadInvoiceClick = async () => {
      setDownloadingInvoice(true);
      try {
        console.log(`/bills/${invoice.id}/pdf`)

        const response = await secureGetPdf(`/bills/${invoice.id}/pdf`, showSnackbar);
        console.log(`/bills/${invoice.id}/pdf`)

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Invoice_${invoice.billNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showSnackbar(t('confirmation.downloadSuccess'), 'success');
      } catch (error) {
        console.error("Failed to download invoice:", error);
      } finally {
        setDownloadingInvoice(false);
      }
    };
  

    const handleDownloadBonSortieClick = async () => {
      setDownloadingBonSortie(true);
      console.log(`/bills/${invoice.id}/exit-voucher/pdf`)
      try {
        const response = await secureGetPdf(`/bills/${invoice.id}/exit-voucher/pdf`, showSnackbar);
      console.log(`/bills/${invoice.id}/exit-voucher/pdf`)
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Bon_Sortie_${invoice.billNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showSnackbar(t('confirmation.downloadSuccess'), 'success');
      } catch (error) {
        console.error("Failed to download bon sortie:", error);
      } finally {
        setDownloadingBonSortie(false);
      }
    };

  const headerActions = (
    <div className="flex items-center gap-3">
      {invoice?.paymentStatus !== "PAID" && (
        <button
          className="flex items-center justify-center bg-[#06D6A0] text-white px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011] disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleMarkPaidClick}
          disabled={markingAsPaid}
        >
          {markingAsPaid ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Chargement...</span>
            </>
          ) : (
            <>
              <img src={checkIcon} alt="check" className="w-5 h-5" />
              {t('actions.mark_as_paid')}
            </>
          )}
        </button>
      )}
      {invoice?.paymentStatus === "PAID" && (
        <>
          <button
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            disabled={downloadingInvoice || downloadingBonSortie}
            className="flex items-center justify-center px-2 h-[40px] bg-white shadow-[0px_20px_80px_#00000011] rounded-[12px] text-primary font-bold space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(downloadingInvoice || downloadingBonSortie) ? (
              <>
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span>Chargement...</span>
              </>
            ) : (
              <>
                <img src={PrintIcon} alt="Print" className="w-5 h-5" />
                <span>{t(`primaryButtons.print`)}</span>
                <img src={ChevronDownIcon} alt="Chevron Down" className="w-4 h-4" />
              </>
            )}
          </button>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={() => setDownloadMenuAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              mt: 0.5,
              '& .MuiPaper-root': {
                borderRadius: '8px',
                padding: '0px 0',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              },
              '& .MuiMenuItem-root': {
                fontSize: '14px',
                fontWeight: '500',
                color: '#043F88',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              },
            }}
          >
            <MenuItem onClick={() => { handleDownloadInvoiceClick(); setDownloadMenuAnchor(null); }}>
              <img src={PrintIcon} alt="Download Invoice" className="w-5 h-5" />
              <span>{t(`primaryButtons.downloadInvoice`)}</span>
            </MenuItem>
            <MenuItem onClick={() => { handleDownloadBonSortieClick(); setDownloadMenuAnchor(null); }}>
              <img src={PrintIcon} alt="Download Bon Sortie" className="w-5 h-5" />
              <span>{t(`primaryButtons.downloadBonSortie`)}</span>
            </MenuItem>
          </Menu>
        </>
        )}
    </div>
  );


  return (
    <div>
      <Helmet>
        <title>Drivnest - {t("titles.invoice_details")}</title>
      </Helmet>

      <div className="flex flex-col mb-[20px]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col w-full">
            <Breadcrumbs />
             <DetailsHeader {...headerProps} rightActions={headerActions} />
         </div>
        </div>
      </div>

      {/* Quote Details Section */}
      {quote && (
        <div className="mt-6">
          <div className="px-0 py-2">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Left: Quote ID */}
              <div className="flex items-center gap-3">
                <h3 className="text-[24px] text-[#043F87] font-semibold">
                  ID {quote.quoteNumber || quote.id}
                </h3>
              </div>

              {/* Right: Totals */}
              <div className="flex flex-wrap items-center gap-6 ml-auto">
              <div className="flex items-center gap-2">
                  <span className="text-[20px] text-[#3771B0] font-semibold">ACOMPTE:</span>
                  <span className="text-[20px] text-[#38485C] font-bold">
                    {formatAmount(quote.advancePayment || 0)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL HT:</span>
                  <span className="text-[20px] text-[#38485C] font-bold">
                    {formatAmount(quote.priceHT || 0)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL TTC:</span>
                  <span className="text-[20px] text-[#38485C] font-bold">
                    {formatAmount(quote.priceTTC || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quote Items */}
          <div className="space-y-2">
            {quote.details?.map((detail, index) => (
              <ProductRow key={index} product={detail} variant="quote" />
            ))}
          </div>
        </div>
      )}


      {showModule && (
        <ModuleWrapper
          moduleType="invoice_payment"
          mode="add"
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={{ billId: invoice.id }}
        />
      )}

      {/* Payment Image Modal */}
      {showPaymentImageModal && invoice?.justificationFile && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]" 
          onClick={() => {
            console.log('Closing modal');
            setShowPaymentImageModal(false);
          }}
        >
          <div className="bg-white rounded-[12px] shadow-lg max-w-4xl w-full mx-4 relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowPaymentImageModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
            >
              <img src={closeIcon} alt="Close" className="w-6 h-6" />
            </button>
            <div className="p-6">
              <h3 className="text-[24px] font-semibold text-[#38485C] mb-4">
                Mode de paiement: {t(`paymentModes.${invoice.paymentMode}`)}
              </h3>
              <div className="flex justify-center">
                <img
                  src={invoice.justificationFile}
                  alt="Payment justification"
                  className="max-w-full max-h-[70vh] object-contain rounded-[12px]"
                  onError={(e) => {
                    e.target.src = '/icons/noImageIcon.svg';
                    e.target.style.width = "200px";
                    e.target.style.height = "200px";
                    e.target.style.objectFit = "contain";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default InvoiceDetails;
